import { ToggleGroup } from "radix-ui";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

type Option<T extends string> = { value: T; label: ReactNode; icon?: ReactNode };

type SegmentedProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: Option<T>[];
  "aria-label": string;
  className?: string;
};

/** A row of mutually exclusive buttons (view modes, simple filters). */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
  ...props
}: SegmentedProps<T>) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      // Radix reports "" when the active item is clicked again; keep the selection.
      onValueChange={(v) => v && onChange(v as T)}
      aria-label={props["aria-label"]}
      className={cn("inline-flex h-9 items-center rounded-lg border bg-surface-muted p-0.5", className)}
    >
      {options.map((o) => (
        <ToggleGroup.Item
          key={o.value}
          value={o.value}
          className="inline-flex h-full cursor-pointer items-center gap-1.5 rounded-md px-3 text-sm font-medium whitespace-nowrap text-muted transition-colors hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none data-[state=on]:bg-surface data-[state=on]:text-fg data-[state=on]:shadow-card [&_svg]:size-4"
        >
          {o.icon}
          {o.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
